import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabaseClient';
import { propertyService } from '../services/propertyService';
import Navbar from '../components/Navbar';
import SEO from '../components/SEO';
import AreaSelectionModal from '../components/AreaSelectionModal';
import SearchFiltersModal from '../components/SearchFiltersModal';
import './Search.css';

const Search = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();
    const [properties, setProperties] = useState([]);
    const [totalResults, setTotalResults] = useState(0);
    const [page, setPage] = useState(parseInt(searchParams.get('page')) || 1);
    const limit = 10;

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Modal State
    const [isAreaModalOpen, setIsAreaModalOpen] = useState(false);
    const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);

    // Initial Filter State from URL
    const [filters, setFilters] = useState({
        location: searchParams.get('location') || '',
        localities: searchParams.get('localities') || '', // Comma separated
        propertyType: searchParams.getAll('type') || [], // multi
        budget: searchParams.get('budget') || '',
        bhk: searchParams.getAll('bhk') || [], // multi
        furnishing: searchParams.getAll('furnishing') || [], // multi
        listingType: searchParams.get('listingType') || '',
    });

    const [sort, setSort] = useState('newest');

    useEffect(() => {
        // Sync state if URL changes externally (optional but good)
    }, [searchParams]);

    useEffect(() => {
        fetchProperties();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters, sort, page]);

    const fetchProperties = async () => {
        setLoading(true);
        setError(null);
        try {
            // Parse budget
            let minPrice, maxPrice;
            if (filters.budget) {
                if (filters.budget.includes('-')) {
                    const parts = filters.budget.split('-');
                    minPrice = parseInt(parts[0]);
                    maxPrice = parseInt(parts[1]);
                } else if (filters.budget.includes('+')) {
                    minPrice = parseInt(filters.budget.replace('+', ''));
                }
            }

            // Parse localities
            const localityList = filters.localities ? filters.localities.split(',').filter(l => l) : [];

            // Parse BHK if array of strings, convert to ints
            const bhkInts = filters.bhk.map(b => parseInt(b));

            const offset = (page - 1) * limit;

            const { properties: data, totalCount } = await propertyService.searchProperties({
                locationText: filters.location,
                localities: localityList,
                propertyTypes: Array.isArray(filters.propertyType) ? filters.propertyType : [filters.propertyType],
                bhkOptions: bhkInts,
                minPrice,
                maxPrice,
                furnishingOptions: Array.isArray(filters.furnishing) ? filters.furnishing : [filters.furnishing],
                listingType: filters.listingType,
                sortBy: sort,
                limit,
                offset
            });

            setProperties(data);
            setTotalResults(totalCount);
        } catch (err) {
            console.error(err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleFilterChange = (key, value) => {
        const newFilters = { ...filters, [key]: value };
        setFilters(newFilters);
        setPage(1);

        // Update URL
        const newParams = new URLSearchParams();
        newParams.set('page', '1');

        if (newFilters.location) newParams.set('location', newFilters.location);
        if (newFilters.localities) newParams.set('localities', newFilters.localities);
        if (newFilters.listingType) newParams.set('listingType', newFilters.listingType);
        if (newFilters.budget) newParams.set('budget', newFilters.budget);

        // Arrays
        if (newFilters.propertyType) {
            const arr = Array.isArray(newFilters.propertyType) ? newFilters.propertyType : [newFilters.propertyType];
            arr.forEach(v => v && newParams.append('type', v));
        }
        if (newFilters.bhk) {
            const arr = Array.isArray(newFilters.bhk) ? newFilters.bhk : [newFilters.bhk];
            arr.forEach(v => v && newParams.append('bhk', v));
        }
        if (newFilters.furnishing) {
            const arr = Array.isArray(newFilters.furnishing) ? newFilters.furnishing : [newFilters.furnishing];
            arr.forEach(v => v && newParams.append('furnishing', v));
        }

        setSearchParams(newParams);
    };

    const handlePageChange = (newPage) => {
        setPage(newPage);
        const newParams = new URLSearchParams(searchParams);
        newParams.set('page', newPage.toString());
        setSearchParams(newParams);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    // Area Management
    const localityList = filters.localities ? filters.localities.split(',').filter(l => l) : [];

    const removeLocality = (loc) => {
        const updated = localityList.filter(l => l !== loc);
        handleFilterChange('localities', updated.join(','));
    };

    const handleAreaModalNext = (selectedAreas) => {
        setIsAreaModalOpen(false);
        handleFilterChange('localities', selectedAreas.join(','));
    };

    const clearAllLocalities = () => {
        handleFilterChange('localities', '');
    };

    // Modal Filters Handler
    const handleModalFiltersApply = (modalFilters) => {
        handleFilterChange('listingType', modalFilters.listingType);
        handleFilterChange('propertyType', modalFilters.propertyTypes);
        handleFilterChange('bhk', modalFilters.bhk);
        handleFilterChange('furnishing', modalFilters.furnishing);
        setIsFilterModalOpen(false);
    };

    // Prepare initial filters for modal
    const modalInitialFilters = {
        listingType: filters.listingType || 'Rent',
        propertyTypes: filters.propertyType || [],
        bhk: filters.bhk || [],
        furnishing: filters.furnishing || [],
        budget: filters.budget || ''
    };

    return (
        <div className="page-container search-page">
            <SEO
                title={`Properties in ${filters.localities || 'All Locations'}`}
                description={`Browse ${properties.length} properties.`}
                path={`/search?${searchParams.toString()}`}
            />
            <Navbar />

            {/* Filter Header */}
            <div className="search-header-bg">
                <div className="search-header-content">
                    {/* Search Bar Row - Matches Mockup */}
                    <div className="search-bar-row">
                        <div className="search-input-container">
                            <i className="fas fa-search search-icon-input"></i>
                            <div className="chips-input-area">
                                {localityList.length === 0 ? (
                                    <div className="placeholder-text" onClick={() => setIsAreaModalOpen(true)}>
                                        Search for properties, locations...
                                    </div>
                                ) : (
                                    <div className="chips-container-search">
                                        {localityList.map(loc => (
                                            <div key={loc} className="search-chip">
                                                <span>{loc}</span>
                                                <button onClick={(e) => { e.stopPropagation(); removeLocality(loc); }}>&times;</button>
                                            </div>
                                        ))}
                                        <button className="add-more-text-btn" onClick={() => setIsAreaModalOpen(true)}>
                                            + Add
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Filter Trigger Button - Icon Style */}
                        <button
                            className="filter-icon-btn"
                            onClick={() => setIsFilterModalOpen(true)}
                        >
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <line x1="4" y1="21" x2="4" y2="14"></line>
                                <line x1="4" y1="10" x2="4" y2="3"></line>
                                <line x1="12" y1="21" x2="12" y2="12"></line>
                                <line x1="12" y1="8" x2="12" y2="3"></line>
                                <line x1="20" y1="21" x2="20" y2="16"></line>
                                <line x1="20" y1="12" x2="20" y2="3"></line>
                                <line x1="1" y1="14" x2="7" y2="14"></line>
                                <line x1="9" y1="8" x2="15" y2="8"></line>
                                <line x1="17" y1="16" x2="23" y2="16"></line>
                            </svg>
                        </button>
                    </div>
                </div>
            </div>

            {/* Results Section */}
            <main className="search-results-container">
                <div className="results-header">
                    <h1 className="results-count">
                        Showing {totalResults} {totalResults === 1 ? 'result' : 'results'} in <span className="highlight-loc">{filters.localities || 'All Locations'}</span>
                    </h1>
                    <div className="sort-dropdown-container">
                        <label>Sort:</label>
                        <select className="sort-select" value={sort} onChange={(e) => setSort(e.target.value)}>
                            <option value="newest">Featured</option>
                            <option value="price_asc">Price: Low to High</option>
                            <option value="price_desc">Price: High to Low</option>
                        </select>
                    </div>
                </div>

                <div className="results-list">
                    {loading ? (
                        <div className="loading">Loading...</div>
                    ) : error ? (
                        <div className="error">{error}</div>
                    ) : properties.length === 0 ? (
                        <div className="no-results">
                            <p>No properties found matching your criteria.</p>
                            <button className="reset-filters-btn" onClick={() => setFilters({
                                location: '', localities: '', propertyType: [], budget: '', bhk: [], furnishing: [], listingType: ''
                            })}>Reset Filters</button>
                        </div>
                    ) : (
                        properties.map(property => (
                            <div
                                key={property.id}
                                className={`search-property-card ${property.status === 'rented' ? 'is-rented' : ''}`}
                            >
                                <div className="card-image-section">
                                    {property.status === 'rented' && (
                                        <div className="rented-badge">RENTED</div>
                                    )}
                                    <button className="favorite-btn">
                                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>
                                        </svg>
                                    </button>
                                    {property.image ? (
                                        <img src={property.image} alt={property.title} onClick={() => navigate(`/properties/${property.id}`)} />
                                    ) : (
                                        <div className="placeholder-image" onClick={() => navigate(`/properties/${property.id}`)}>No Image</div>
                                    )}
                                </div>
                                <div className="card-content-section">
                                    <div className="card-header-row">
                                        <h2 className="card-title" onClick={() => navigate(`/properties/${property.id}`)}>
                                            {property.title}
                                        </h2>
                                        <div className="card-price">
                                            ₹{property.price ? property.price.toLocaleString() : '0'} <span className="price-suffix">/ mo</span>
                                        </div>
                                    </div>

                                    <div className="card-location-row">
                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                                            <circle cx="12" cy="10" r="3"></circle>
                                        </svg>
                                        <span>{property.location || 'Bangalore'}</span>
                                    </div>

                                    <br />

                                    <div className="card-specs-row">
                                        <div className="spec-item-new">
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <path d="M3 21h18M5 21V7l8-4 8 4v14M17 21v-8.5a.5.5 0 0 0-.5-.5h-3a.5.5 0 0 0-.5.5V21"></path>
                                            </svg>
                                            <span>{property.sqft || '---'} sqft</span>
                                        </div>
                                        <div className="spec-item-new">
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <path d="M4 21v-8a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v8"></path>
                                                <path d="M2 6h20"></path>
                                                <path d="M12 2v4"></path>
                                            </svg>
                                            <span>{property.bhk} BHK</span>
                                        </div>
                                        <div className="spec-item-new">
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <rect x="2" y="3" width="20" height="14" rx="2" ry="2"></rect>
                                                <line x1="8" y1="21" x2="16" y2="21"></line>
                                                <line x1="12" y1="17" x2="12" y2="21"></line>
                                            </svg>
                                            <span>{property.furnishing || 'Unfurnished'}</span>
                                        </div>
                                    </div>

                                    <br />

                                    <div className="card-actions-row">
                                        <button className="action-btn secondary">Contact</button>
                                        <button className="action-btn primary" onClick={() => navigate(`/properties/${property.id}`)}>View Details</button>
                                    </div>
                                </div>
                            </div>
                        ))
                    )}
                </div>

                {/* Pagination */}
                {totalResults > limit && (
                    <div className="pagination-container">
                        <button
                            className="page-btn prev"
                            disabled={page === 1}
                            onClick={() => handlePageChange(page - 1)}
                        >&lt;</button>

                        {(() => {
                            const totalPages = Math.ceil(totalResults / limit);
                            let pages = [];
                            if (totalPages <= 5) {
                                for (let i = 1; i <= totalPages; i++) pages.push(i);
                            } else {
                                if (page <= 3) {
                                    pages = [1, 2, 3, 4, '...', totalPages];
                                } else if (page >= totalPages - 2) {
                                    pages = [1, '...', totalPages - 3, totalPages - 2, totalPages - 1, totalPages];
                                } else {
                                    pages = [1, '...', page - 1, page, page + 1, '...', totalPages];
                                }
                            }

                            return pages.map((p, index) => (
                                p === '...' ? (
                                    <span key={`ellipsis-${index}`} className="page-ellipsis">...</span>
                                ) : (
                                    <button
                                        key={p}
                                        className={`page-btn ${page === p ? 'active' : ''}`}
                                        onClick={() => handlePageChange(p)}
                                    >
                                        {p}
                                    </button>
                                )
                            ));
                        })()}

                        <button
                            className="page-btn next"
                            disabled={page === Math.ceil(totalResults / limit)}
                            onClick={() => handlePageChange(page + 1)}
                        >&gt;</button>
                    </div>
                )}
            </main>

            <AreaSelectionModal
                isOpen={isAreaModalOpen}
                onClose={() => setIsAreaModalOpen(false)}
                onNext={handleAreaModalNext}
                initialSelectedAreas={localityList}
            />

            <SearchFiltersModal
                isOpen={isFilterModalOpen}
                onClose={() => setIsFilterModalOpen(false)}
                selectedAreas={localityList}
                onSearch={handleModalFiltersApply}
                initialFilters={modalInitialFilters}
            />
        </div>
    );
};

export default Search;
